import { useRef, useState } from 'react';
import { notifyError } from '@/core/notify/notify';
import { phase1Service } from '@/core/services/phase1';
import { trialBalanceService } from '@/core/services/trialBalance';
import type {
  ImportExceptionAction,
  ImportExceptionRow,
  ResolveImportExceptionResponse,
} from '@/core/models/phase1.models';
import type { AccountTypeOption } from '@/core/models/trial-balance.models';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { validateForm } from '@/features/manage/formRules';
import {
  CancelButton,
  flattenReportingCodes,
  numberFromInput,
  numberToInput,
  useAlive,
  useLive,
  useOnInit,
} from './common';

export interface ResolveExceptionDialogData {
  periodId: string;
  exception: ImportExceptionRow;
  /** APT334: the client the import belongs to - Add to chart creates the account in this client's chart. */
  clientId?: string | null;
  /** APT334: the batch is live (committed) and holds this line back. The rest of it is posted, so the line cannot be ignored. */
  batchCommitted?: boolean;
}

const MAP = { id: 1, name: 'Map to a nominal' };
const CORRECT = { id: 2, name: 'Correct the amount' };
const IGNORE = { id: 3, name: 'Ignore this row' };
/** APT334 (the client's answer: "provide an option to add COA from Import exceptions"). */
const ADD_TO_CHART = { id: 4, name: 'Add the account to the chart' };

const NOT_IN_CHART = 'NOT_IN_CHART';
const RESERVED_CODE = 'RESERVED_CODE';

/**
 * What can be done with a row. A batch-level exception (no staged row behind it) has nothing for Map or Correct to act on - the API rejects
 * both with "raised against the whole batch, so it cannot be resolved row by row". APT334: an account the chart lacks can be mapped or added
 * to the chart (never "corrected": an amount fix does not make it postable - the API refuses it), a reserved code only mapped, and a line
 * of a live batch cannot be ignored (the rest is already posted, so dropping it would leave the trial balance out of balance for good).
 */
function actionOptionsFor(data: ResolveExceptionDialogData | null): { id: number; name: string }[] {
  const exception = data?.exception;
  if (!exception || exception.importStagingLineId === null) return [IGNORE];
  const offChart = exception.errorCode === NOT_IN_CHART || exception.errorCode === RESERVED_CODE;
  const options = [MAP];
  if (exception.errorCode === NOT_IN_CHART) options.push(ADD_TO_CHART);
  if (!offChart) options.push(CORRECT);
  if (!data?.batchCommitted) options.push(IGNORE);
  return options;
}

interface Model {
  action: ImportExceptionAction;
  nominalCode: string;
  description: string;
  reportingCodeId: string | null;
  debit: number | null;
  credit: number | null;
  /** The amount boxes keep what was typed ("12." must stay "12."); the numbers above are what is sent. */
  debitText?: string;
  creditText?: string;
  note: string;
  reportingCodes: { id: string; name: string }[];
  /** APT334, Add to chart: the new account's name and type, and the types the client's chart may use. */
  accountName: string;
  accountTypeId: number | null;
  accountTypes: { id: number; name: string }[];
  /** True after a save was refused: the mandatory fields that are still empty are outlined red. */
  submitted: boolean;
}

/** The longest note the import exception holds. */
const NOTE_MAX = 500;
/** Amount columns are DECIMAL(19,4): 15 whole digits and 4 decimals. */
const AMOUNT_BOX = { charMode: 'number', allowDecimal: true, maxIntDigits: 15, maxDecimals: 4 } as const;
/** The number behind amount text; '' and a lone '.' are no amount. */
const amountFromText = (text: string): number | null => {
  const value = numberFromInput(text);
  return value === null || Number.isNaN(value) ? null : value;
};

function initialModel(data: ResolveExceptionDialogData | null): Model {
  const model: Model = {
    action: 1,
    nominalCode: '',
    description: '',
    reportingCodeId: null,
    debit: 0,
    credit: 0,
    note: '',
    reportingCodes: [],
    accountName: '',
    accountTypeId: null,
    accountTypes: [],
    submitted: false,
  };
  const exception = data?.exception;
  if (exception) {
    model.nominalCode = exception.code ?? '';
    model.description = exception.description ?? '';
    model.accountName = exception.description?.trim() || exception.code || '';
    model.debit = exception.debit;
    model.credit = exception.credit;
    // A batch-level exception (no staged row) can only be ignored. An unknown nominal, or an account the chart lacks, starts on Map.
    model.action =
      exception.importStagingLineId === null
        ? 3
        : exception.errorCode === 'UNKNOWN_NOMINAL' || exception.errorCode === NOT_IN_CHART || exception.errorCode === RESERVED_CODE
          ? 1
          : 2;
  }
  return model;
}

/** The account types this client's chart may use: the options carry each type's client types and this client's. */
function typesForClient(options: { accountTypes: AccountTypeOption[]; clientType?: number | null }) {
  return options.accountTypes
    .filter((t) => !t.clientTypes?.length || options.clientType == null || t.clientTypes.includes(options.clientType))
    .map((t) => ({ id: t.id, name: `${t.name} (${t.groupName})` }));
}

/** Map the row to a nominal, add its account to the chart, correct its amount, or ignore it (Angular ResolveExceptionDialogComponent). */
export function ResolveExceptionDialog({
  data,
  dialogRef,
}: DialogComponentProps<ResolveExceptionDialogData, boolean | ResolveImportExceptionResponse>) {
  const alive = useAlive();
  const [model, set, modelRef] = useLive<Model>(initialModel(data));
  const isSaving = useRef(false);
  const [busy, setBusy] = useState(false);
  const actionOptions = actionOptionsFor(data);

  useOnInit(() => {
    if (!data?.periodId) return;
    phase1Service
      .getReportingCodes(data.periodId)
      .then((tree) => {
        if (alive.current) set({ reportingCodes: flattenReportingCodes(tree) });
      })
      .catch(() => {
        if (alive.current) set({ reportingCodes: [] });
      });

    // Add to chart needs the account types the client's chart may use.
    if (data.exception.errorCode === NOT_IN_CHART && data.clientId) {
      trialBalanceService
        .getAccountOptions(data.clientId)
        .then((options) => {
          if (alive.current) set({ accountTypes: typesForClient(options) });
        })
        .catch((error) => {
          if (alive.current) notifyError(error);
        });
    }
  });

  const onSave = () => {
    if (isSaving.current || !data?.periodId) {
      return;
    }
    const m = modelRef.current;

    set({ submitted: true });

    if (m.action === 1 && !m.nominalCode?.trim()) {
      validateForm(false);
      return;
    }

    if (m.action === 4 && !m.accountName?.trim()) {
      validateForm(false);
      return;
    }

    if (m.action === 4 && !m.accountTypeId) {
      validateForm(false);
      return;
    }

    if (m.action === 2 && (m.debit ?? 0) > 0 && (m.credit ?? 0) > 0) {
      notifyError('Enter either a debit or a credit, not both.');
      return;
    }

    isSaving.current = true;
    setBusy(true);
    phase1Service
      .resolveException(data.periodId, data.exception.importExceptionId, {
        action: m.action,
        nominalCode: m.action === 1 ? m.nominalCode.trim() : null,
        description: m.action === 1 ? m.description?.trim() || null : m.action === 4 ? m.accountName.trim() : null,
        reportingCodeId: m.action === 1 ? m.reportingCodeId : null,
        debit: m.action === 2 ? Number(m.debit) : null,
        credit: m.action === 2 ? Number(m.credit) : null,
        note: m.note?.trim() || null,
        expectedETag: data.exception.eTag,
        // Only Add to chart carries an account type.
        ...(m.action === 4 ? { accountTypeId: m.accountTypeId } : {}),
      })
      // A resolution that posted lines into a live batch hands its response back, so the screen can say how many.
      .then((response) => dialogRef.close(response?.linesPosted ? response : true))
      .catch((error) => {
        isSaving.current = false;
        if (alive.current) {
          setBusy(false);
          notifyError(error);
        }
      });
  };

  return (
    <div className="mat-dialog-wrapper new-account">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Resolve Import Exception</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field">
              <p className="fw-bold">Exception</p>
              <p>{data?.exception?.errorMessage}</p>
            </div>

            <div className="form-field">
              <p className="fw-bold">Action</p>
              <Select
                className="w-434"
                openOnFocus
                panelClass="myPanelClass"
                items={actionOptions}
                bindLabel="name"
                bindValue="id"
                clearable={false}
                searchable={false}
                name="action"
                value={model.action}
                onChange={(value) => set({ action: value as ImportExceptionAction })}
              />
            </div>

            {model.action === 1 && (
              <div>
                <div className="form-field">
                  <p className="fw-bold">
                    Nominal Code <RequiredMark />
                  </p>
                  <InputField
                    fieldClassName="w-434"
                    invalid={model.submitted && !model.nominalCode.trim()}
                    name="nominalCode"
                    charMode="alphanumeric"
                    value={model.nominalCode}
                    onChange={(event) => set({ nominalCode: event.target.value })}
                  />
                </div>
                <div className="form-field">
                  <p className="fw-bold">Description</p>
                  <InputField
                    fieldClassName="w-434"
                    name="description"
                    charMode="text"
                    value={model.description}
                    onChange={(event) => set({ description: event.target.value })}
                  />
                </div>
                <div className="form-field">
                  <p className="fw-bold">Reporting Code (optional)</p>
                  <Select
                    className="w-434"
                    openOnFocus
                    panelClass="myPanelClass"
                    placeholder="Select Reporting Code"
                    items={model.reportingCodes}
                    bindLabel="name"
                    bindValue="id"
                    name="reportingCodeId"
                    value={model.reportingCodeId}
                    onChange={(value) => set({ reportingCodeId: (value as string | null) ?? null })}
                  />
                </div>
              </div>
            )}

            {model.action === 4 && (
              <div>
                <div className="form-field">
                  <p className="fw-bold">Account Code</p>
                  <p>{data?.exception.code}</p>
                </div>
                <div className="form-field">
                  <p className="fw-bold">
                    Account Name <RequiredMark />
                  </p>
                  <InputField
                    fieldClassName="w-434"
                    invalid={model.submitted && !model.accountName.trim()}
                    name="accountName"
                    charMode="text"
                    value={model.accountName}
                    onChange={(event) => set({ accountName: event.target.value })}
                  />
                </div>
                <div className="form-field">
                  <p className="fw-bold">
                    Account Type <RequiredMark />
                  </p>
                  <Select
                    className={`w-434${model.submitted && !model.accountTypeId ? ' ng-invalid ng-touched' : ''}`}
                    openOnFocus
                    panelClass="myPanelClass"
                    placeholder="Select account type"
                    items={model.accountTypes}
                    bindLabel="name"
                    bindValue="id"
                    name="accountTypeId"
                    value={model.accountTypeId}
                    onChange={(value) => set({ accountTypeId: (value as number | null) ?? null })}
                  />
                </div>
                <p className="text-gray font-size-12">
                  The account is added to this client&apos;s chart of accounts, and the line is then posted.
                </p>
              </div>
            )}

            {model.action === 2 && (
              <div>
                <div className="form-field">
                  <p className="fw-bold">Debit</p>
                  <InputField
                    fieldClassName="w-434"
                    {...AMOUNT_BOX}
                    name="debit"
                    value={model.debitText ?? numberToInput(model.debit)}
                    onChange={(event) => set({ debit: amountFromText(event.target.value), debitText: event.target.value })}
                  />
                </div>
                <div className="form-field">
                  <p className="fw-bold">Credit</p>
                  <InputField
                    fieldClassName="w-434"
                    {...AMOUNT_BOX}
                    name="credit"
                    value={model.creditText ?? numberToInput(model.credit)}
                    onChange={(event) => set({ credit: amountFromText(event.target.value), creditText: event.target.value })}
                  />
                </div>
              </div>
            )}

            <div className="form-field">
              <p className="fw-bold">Note</p>
              <InputField
                fieldClassName="w-434"
                name="note"
                maxLength={NOTE_MAX}
                value={model.note}
                onChange={(event) => set({ note: event.target.value })}
              />
            </div>
          </div>
          <div className="sidenav-footer">
            <div className="d-flex align-items-center gap-10">
              <Button className="action-button primary-button" busy={busy} onClick={onSave}>
                Resolve
              </Button>
              <CancelButton />
            </div>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
